import { localDateString } from '../../../../utils/helpers/localDate';
import { useState, useEffect, useMemo } from 'react';
import { calculateItemTax } from '../../../../utils/helpers/taxCalculation';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { createCreditDebitNote, updateCreditDebitNote, getCreditDebitNoteDetails, clearCreditDebitNoteState } from '../../../../redux/actions/SocietyPortal/CreditDebitNoteAction/CreditDebitNoteAction';
import { getInvoicesByFlat } from '../../../../redux/actions/SocietyPortal/InvoiceAction/InvoiceAction';
import { getInvoiceTypes } from '../../../../redux/actions/SocietyPortal/InvoiceTypeAction/InvoiceTypeAction';
import { getWingsDropdown, getFlatsWithOwnership } from '../../../../redux/actions/WingsAndFlatsActions/WingsAndFlatsAction';
import { getTariffHeadsWithDetails } from '../../../../redux/actions/SocietyPortal/TariffHeadAction/TariffHeadAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_CREDIT_NOTES, SOCIETY_PORTAL_DEBIT_NOTES } from '../../../../Routing/routes';
import { addDaysToDateString, getDueDateError } from '../../../../utils/helpers/universalFunctions';
import { formatIstDate } from '../../../../utils/helpers/istDate';

const CreateCreditDebitNote = ({ noteType = 'CREDIT_NOTE', noteId }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const societyId = selectedSocietyStorer.getSocietyId();

    const isEditMode = !!noteId;
    const isCreditNote = noteType === 'CREDIT_NOTE';
    const noteLabel = isCreditNote ? 'Credit Note' : 'Debit Note';
    const accentColor = isCreditNote ? 'emerald' : 'orange';
    const listRoute = isCreditNote ? SOCIETY_PORTAL_CREDIT_NOTES : SOCIETY_PORTAL_DEBIT_NOTES;

    const { invoiceTypesData } = useSelector(state => state.InvoiceTypeReducers || {});
    const { wingsDropdown, flatsWithOwnership, flatsWithOwnershipLoading } = useSelector(state => state.WingsAndFlatsReducers || {});
    const { tariffHeadsWithDetails } = useSelector(state => state.TariffHeadReducer || {});
    const { createLoading, updateLoading, noteDetails, detailsLoading } = useSelector(state => state.CreditDebitNoteReducer || {});
    const { invoicesByFlat, invoicesByFlatLoading } = useSelector(state => state.InvoiceReducer || {});

    const submitLoading = isEditMode ? updateLoading : createLoading;

    const [formData, setFormData] = useState({
        invoice_type_id: '',
        wing_id: '',
        flat_id: '',
        invoice_id: '',
        issue_date: localDateString(),
        due_date: '',
        reason: '',
        remarks: '',
    });

    const [lineItems, setLineItems] = useState([]);
    const [selectedFlatMembers, setSelectedFlatMembers] = useState([]);

    const tariffHeadsArray = useMemo(() => {
        if (Array.isArray(tariffHeadsWithDetails)) return tariffHeadsWithDetails;
        if (tariffHeadsWithDetails?.data && Array.isArray(tariffHeadsWithDetails.data)) return tariffHeadsWithDetails.data;
        return [];
    }, [tariffHeadsWithDetails]);

    const chargeHeads = useMemo(() => {
        return tariffHeadsArray.filter(head => ['CHARGE', 'DISCOUNT', 'INTEREST'].includes(head.headType?.type_name));
    }, [tariffHeadsArray]);

    // Fetch note details in edit mode
    useEffect(() => {
        if (isEditMode) {
            dispatch(getCreditDebitNoteDetails({ id: noteId }));
        }
        return () => { if (isEditMode) dispatch(clearCreditDebitNoteState()); };
    }, [dispatch, noteId, isEditMode]);

    // Pre-fill form when note details are loaded in edit mode
    useEffect(() => {
        if (isEditMode && noteDetails) {
            setFormData({
                invoice_type_id: noteDetails.invoice_type_id || '',
                wing_id: '',
                flat_id: noteDetails.flat_id || '',
                invoice_id: noteDetails.invoice_id || '',
                issue_date: noteDetails.issue_date ? noteDetails.issue_date.split('T')[0] : '',
                due_date: noteDetails.due_date ? noteDetails.due_date.split('T')[0] : '',
                reason: noteDetails.reason || '',
                remarks: noteDetails.remarks || '',
            });
            if (noteDetails.items?.length) {
                const systemTypes = ['CGST', 'SGST', 'IGST', 'ROUND_OFF', 'ADJUSTMENT', 'PRINCIPAL_ARREAR', 'DEBIT_NOTE_ARREAR'];
                setLineItems(noteDetails.items
                    .filter(i => i.is_active !== 0 && !systemTypes.includes(i.tariffHead?.headType?.type_name))
                    .map(i => ({
                        tariff_head_id: String(i.head_id),
                        amount: String(i.base_amount || ''),
                    })));
            }
        }
    }, [isEditMode, noteDetails]);

    useEffect(() => {
        dispatch(getInvoiceTypes({ society_id: societyId }));
        if (!isEditMode) {
            dispatch(getWingsDropdown({ society_id: societyId }));
        }
    }, [dispatch, societyId, isEditMode]);

    useEffect(() => {
        if (formData.invoice_type_id) {
            dispatch(getTariffHeadsWithDetails({ society_id: societyId, invoice_type_id: formData.invoice_type_id }));
        }
    }, [dispatch, societyId, formData.invoice_type_id]);

    useEffect(() => {
        if (formData.wing_id) {
            dispatch(getFlatsWithOwnership({ society_id: societyId, wing_id: formData.wing_id }));
            setFormData(prev => ({ ...prev, flat_id: '' }));
            setSelectedFlatMembers([]);
        }
    }, [dispatch, societyId, formData.wing_id]);

    useEffect(() => {
        if (formData.flat_id && flatsWithOwnership?.length) {
            const flat = flatsWithOwnership.find(f => f.flat_id === parseInt(formData.flat_id));
            setSelectedFlatMembers(flat?.ownerships || []);
        } else {
            setSelectedFlatMembers([]);
        }
    }, [formData.flat_id, flatsWithOwnership]);

    // Fetch invoices for selected flat + invoice type
    useEffect(() => {
        const flatId = isEditMode ? (noteDetails?.flat_id || formData.flat_id) : formData.flat_id;
        if (flatId && formData.invoice_type_id) {
            dispatch(getInvoicesByFlat({
                flat_id: flatId,
                include_void: 0,
                invoice_type_id: parseInt(formData.invoice_type_id),
            }));
        }
    }, [dispatch, formData.flat_id, formData.invoice_type_id, isEditMode, noteDetails?.flat_id]);

    const selectedInvoice = useMemo(() => {
        if (!formData.invoice_id || !invoicesByFlat?.length) return null;
        return invoicesByFlat.find(inv => inv.invoice_id === parseInt(formData.invoice_id)) || null;
    }, [formData.invoice_id, invoicesByFlat]);

    const formatCurrency = (amount) => `Rs ${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
            ...(name === 'wing_id' ? { flat_id: '', invoice_id: '' } : {}),
            ...(name === 'flat_id' ? { invoice_id: '' } : {}),
            ...(name === 'invoice_type_id' ? { invoice_id: '' } : {}),
            // Drop a due date that is no longer after the issue date
            ...(name === 'issue_date' && getDueDateError(value, prev.due_date) ? { due_date: '' } : {}),
        }));
    };

    // Debit notes carry a due date, which must be after the issue date
    const dueDateError = isCreditNote ? '' : getDueDateError(formData.issue_date, formData.due_date, 'issue date');
    const minDueDate = addDaysToDateString(formData.issue_date, 1);

    const addLineItem = () => {
        setLineItems(prev => [...prev, { tariff_head_id: '', amount: '' }]);
    };

    const updateLineItem = (index, field, value) => {
        setLineItems(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    const removeLineItem = (index) => {
        setLineItems(prev => prev.filter((_, i) => i !== index));
    };

    const lineItemsWithGst = useMemo(() => {
        return lineItems.map(item => {
            const baseAmount = parseFloat(item.amount) || 0;
            const head = chargeHeads.find(h => String(h.head_id) === String(item.tariff_head_id));
            const taxRule = head?.taxRule || null;
            // Applying the raw rates here ignored the rule's exemption
            // threshold, so a "GST 18% with 7500 Exemption" head previewed 18%
            // on an amount under the limit while the server — which does honour
            // the threshold — saved the note with no GST. calculateItemTax
            // mirrors the server, so the preview and the printed note agree.
            let cgst = 0, sgst = 0, igst = 0;
            if (taxRule && baseAmount > 0) {
                ({ cgst, sgst, igst } = calculateItemTax(baseAmount, taxRule));
            }
            return { ...item, baseAmount, cgst, sgst, igst, total: baseAmount + cgst + sgst + igst, taxRule };
        });
    }, [lineItems, chargeHeads]);

    const totalBase = lineItemsWithGst.reduce((sum, item) => sum + item.baseAmount, 0);
    const totalCgst = lineItemsWithGst.reduce((sum, item) => sum + item.cgst, 0);
    const totalSgst = lineItemsWithGst.reduce((sum, item) => sum + item.sgst, 0);
    const totalIgst = lineItemsWithGst.reduce((sum, item) => sum + item.igst, 0);
    const subtotalWithTax = totalBase + totalCgst + totalSgst + totalIgst;
    const roundOff = Math.round(subtotalWithTax) - subtotalWithTax;
    const totalAmount = subtotalWithTax + roundOff;

    // A note with no priced head carries no amount, so it has nothing to adjust against
    const pricedItems = lineItems.filter(item => item.tariff_head_id && parseFloat(item.amount) > 0);
    const itemsError = pricedItems.length === 0
        ? `Add at least one charge head with an amount to create a ${noteLabel.toLowerCase()}.`
        : totalAmount <= 0
            ? 'Total amount must be greater than zero.'
            : '';

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.invoice_type_id || !formData.issue_date || !formData.reason || (!isCreditNote && !formData.due_date)) {
            alert('Please fill all required fields.');
            return;
        }
        if (!isEditMode && !formData.flat_id) {
            alert('Please fill all required fields.');
            return;
        }
        if (dueDateError) {
            alert(dueDateError);
            return;
        }

        const items = lineItems.filter(item => item.tariff_head_id && item.amount).map(item => ({
            tariff_head_id: parseInt(item.tariff_head_id),
            amount: parseFloat(item.amount) || 0,
        }));

        if (itemsError) {
            alert(itemsError);
            return;
        }

        if (isEditMode) {
            dispatch(updateCreditDebitNote({
                id: noteId,
                invoice_type_id: parseInt(formData.invoice_type_id),
                invoice_id: formData.invoice_id ? parseInt(formData.invoice_id) : null,
                issue_date: formData.issue_date,
                due_date: formData.due_date || undefined,
                reason: formData.reason,
                remarks: formData.remarks || undefined,
                items,
                onSuccess: () => navigate(SOCIETY_PORTAL + listRoute),
            }));
        } else {
            dispatch(createCreditDebitNote({
                society_id: societyId,
                note_type: noteType,
                invoice_type_id: parseInt(formData.invoice_type_id),
                flat_id: parseInt(formData.flat_id),
                invoice_id: formData.invoice_id ? parseInt(formData.invoice_id) : undefined,
                issue_date: formData.issue_date,
                due_date: formData.due_date || undefined,
                reason: formData.reason,
                remarks: formData.remarks || undefined,
                items,
                onSuccess: () => navigate(SOCIETY_PORTAL + listRoute),
            }));
        }
    };

    return (
        <>
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-6">
                <div className="tw-flex tw-items-center tw-gap-3">
                    <button onClick={() => navigate(-1)} className="tw-p-2 tw-rounded-lg hover:tw-bg-gray-100 tw-text-gray-500"><Icon icon="mdi:arrow-left" className="tw-text-xl" /></button>
                    <div>
                        <h1 className="tw-text-lg tw-font-semibold tw-text-gray-800">{isEditMode ? 'Edit' : 'Create'} {noteLabel}</h1>
                        <p className="tw-text-xs tw-text-gray-500">{isEditMode ? `Update ${noteLabel.toLowerCase()} details` : `Issue a new ${noteLabel.toLowerCase()}`}</p>
                    </div>
                </div>
            </div>

            {isEditMode && detailsLoading && (
                <div className="tw-flex tw-items-center tw-justify-center tw-py-12">
                    <Icon icon="mdi:loading" className="tw-text-3xl tw-text-gray-400 tw-animate-spin" />
                </div>
            )}

            {(!isEditMode || !detailsLoading) && <form onSubmit={handleSubmit} className="tw-space-y-6">
                {/* Basic Info */}
                <div className="tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-200 tw-p-5">
                    <h2 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-4 tw-flex tw-items-center tw-gap-2">
                        <Icon icon="mdi:information-outline" className="tw-text-gray-400" /> Basic Information
                    </h2>
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4">
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Invoice Type *</label>
                            <select name="invoice_type_id" value={formData.invoice_type_id} onChange={handleChange} required className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500">
                                <option value="">Select Type</option>
                                {(invoiceTypesData || []).map(t => <option key={t.invoice_type_id} value={t.invoice_type_id}>{t.invoice_title}</option>)}
                            </select>
                        </div>
                        {isEditMode ? (
                            <div>
                                <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Flat</label>
                                <div className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-bg-gray-50 tw-text-gray-700">
                                    {noteDetails?.member_details?.flat_no || noteDetails?.flat?.flat_number || '-'}
                                </div>
                            </div>
                        ) : (
                            <>
                                <div>
                                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Wing *</label>
                                    <select name="wing_id" value={formData.wing_id} onChange={handleChange} required className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500">
                                        <option value="">Select Wing</option>
                                        {(wingsDropdown || []).map(w => <option key={w.wing_id} value={w.wing_id}>{w.title}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Flat *</label>
                                    <select name="flat_id" value={formData.flat_id} onChange={handleChange} required disabled={!formData.wing_id || flatsWithOwnershipLoading} className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 disabled:tw-opacity-50">
                                        <option value="">{flatsWithOwnershipLoading ? 'Loading...' : 'Select Flat'}</option>
                                        {(flatsWithOwnership || []).map(f => <option key={f.flat_id} value={f.flat_id}>{f.flat_number} - {f.flat_type} ({f.ownership_type})</option>)}
                                    </select>
                                </div>
                            </>
                        )}
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Issue Date *</label>
                            <input type="date" name="issue_date" value={formData.issue_date} onChange={handleChange} required className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500" />
                        </div>
                        {!isCreditNote && (
                            <div>
                                <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Due Date *</label>
                                <input type="date" name="due_date" value={formData.due_date} min={minDueDate || undefined} onChange={handleChange} required className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-ring-1 ${dueDateError ? 'tw-border-red-400 focus:tw-border-red-500 focus:tw-ring-red-500' : 'tw-border-gray-200 focus:tw-border-blue-500 focus:tw-ring-blue-500'}`} />
                                {dueDateError && <p className="tw-mt-1 tw-text-xs tw-text-red-600">{dueDateError}</p>}
                            </div>
                        )}
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Reference Invoice</label>
                            <select name="invoice_id" value={formData.invoice_id} onChange={handleChange} disabled={!formData.flat_id || !formData.invoice_type_id || invoicesByFlatLoading} className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 disabled:tw-opacity-50">
                                <option value="">{invoicesByFlatLoading ? 'Loading invoices...' : !formData.flat_id || !formData.invoice_type_id ? 'Select flat & type first' : 'None (standalone)'}</option>
                                {(invoicesByFlat || []).map(inv => (
                                    <option key={inv.invoice_id} value={inv.invoice_id}>
                                        {inv.document_number} - {formatCurrency(inv.net_payable)} ({inv.status})
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Selected Invoice Info */}
                    {selectedInvoice && (
                        <div className="tw-mt-4 tw-p-4 tw-bg-blue-50 tw-rounded-lg tw-border tw-border-blue-100">
                            <p className="tw-text-xs tw-font-semibold tw-text-blue-700 tw-uppercase tw-tracking-wider tw-mb-3 tw-flex tw-items-center tw-gap-1.5">
                                <Icon icon="mdi:file-document-outline" className="tw-text-sm" /> Referenced Invoice
                            </p>
                            <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-3">
                                <div>
                                    <p className="tw-text-[11px] tw-text-gray-500">Invoice No</p>
                                    <p className="tw-text-sm tw-font-semibold tw-text-gray-800">{selectedInvoice.document_number}</p>
                                </div>
                                <div>
                                    <p className="tw-text-[11px] tw-text-gray-500">Bill Date</p>
                                    <p className="tw-text-sm tw-text-gray-700">{formatIstDate(selectedInvoice.bill_date)}</p>
                                </div>
                                <div>
                                    <p className="tw-text-[11px] tw-text-gray-500">Net Payable</p>
                                    <p className="tw-text-sm tw-font-semibold tw-text-gray-800">{formatCurrency(selectedInvoice.net_payable)}</p>
                                </div>
                                <div>
                                    <p className="tw-text-[11px] tw-text-gray-500">Balance Due</p>
                                    <p className={`tw-text-sm tw-font-semibold ${parseFloat(selectedInvoice.balance_due) > 0 ? 'tw-text-red-600' : 'tw-text-emerald-600'}`}>{formatCurrency(selectedInvoice.balance_due)}</p>
                                </div>
                                <div>
                                    <p className="tw-text-[11px] tw-text-gray-500">Status</p>
                                    <span className={`tw-inline-block tw-px-2 tw-py-0.5 tw-rounded tw-text-[11px] tw-font-medium ${selectedInvoice.status === 'PAID' ? 'tw-bg-emerald-100 tw-text-emerald-700' : selectedInvoice.status === 'POSTED' ? 'tw-bg-blue-100 tw-text-blue-700' : 'tw-bg-gray-100 tw-text-gray-700'}`}>{selectedInvoice.status}</span>
                                </div>
                                <div>
                                    <p className="tw-text-[11px] tw-text-gray-500">Member</p>
                                    <p className="tw-text-sm tw-text-gray-700">{selectedInvoice.member_details?.name || '-'}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {formData.flat_id && selectedFlatMembers.length > 0 && (
                        <div className="tw-mt-4 tw-pt-4 tw-border-t tw-border-gray-100">
                            <p className="tw-text-xs tw-font-semibold tw-text-gray-400 tw-uppercase tw-tracking-wider tw-mb-2">
                                Members / Owners
                            </p>
                            <div className="tw-flex tw-flex-wrap tw-gap-2">
                                {selectedFlatMembers.map(ownership => {
                                    const name = ownership.entity?.entity_type === 'I'
                                        ? `${ownership.entity?.first_name || ''} ${ownership.entity?.last_name || ''}`.trim()
                                        : ownership.entity?.company_name || '-';
                                    const isIndividual = ownership.entity?.entity_type === 'I';
                                    return (
                                        <div
                                            key={ownership.flat_ownership_id}
                                            className="tw-inline-flex tw-items-center tw-gap-2 tw-pl-1.5 tw-pr-3 tw-py-1 tw-bg-white tw-border tw-border-gray-200 tw-rounded-full tw-shadow-sm"
                                        >
                                            <div className={`tw-w-6 tw-h-6 tw-rounded-full tw-bg-${accentColor}-100 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0`}>
                                                <Icon icon={isIndividual ? 'mdi:account' : 'mdi:office-building'} className={`tw-text-${accentColor}-600 tw-text-xs`} />
                                            </div>
                                            <span className="tw-text-xs tw-font-semibold tw-text-gray-800">{name}</span>
                                            <span className="tw-text-[10px] tw-text-gray-400">{isIndividual ? 'Individual' : 'Company'}</span>
                                            <span className={`tw-text-[10px] tw-font-bold tw-text-${accentColor}-600 tw-bg-${accentColor}-50 tw-border tw-border-${accentColor}-100 tw-px-1.5 tw-py-0.5 tw-rounded-full`}>
                                                {ownership.ownership_share}%
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                {/* Reason */}
                <div className="tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-200 tw-p-5">
                    <h2 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-4 tw-flex tw-items-center tw-gap-2">
                        <Icon icon="mdi:text-box-outline" className="tw-text-gray-400" /> Reason & Remarks
                    </h2>
                    <div className="tw-space-y-4">
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Reason *</label>
                            <textarea name="reason" value={formData.reason} onChange={handleChange} required rows={2} placeholder={`Enter reason for ${noteLabel.toLowerCase()}...`} className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 tw-resize-none" />
                        </div>
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Remarks</label>
                            <textarea name="remarks" value={formData.remarks} onChange={handleChange} rows={2} placeholder="Optional remarks..." className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 tw-resize-none" />
                        </div>
                    </div>
                </div>

                {/* Line Items */}
                <div className="tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-200 tw-p-5">
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                        <h2 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-flex tw-items-center tw-gap-2">
                            <Icon icon="mdi:format-list-bulleted" className="tw-text-gray-400" /> Line Items
                        </h2>
                        <button type="button" onClick={addLineItem} className="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-bg-blue-600 tw-text-white tw-rounded-md tw-text-xs tw-font-medium hover:tw-bg-blue-700">
                            <Icon icon="mdi:plus" /> Add Item
                        </button>
                    </div>

                    {lineItems.length > 0 ? (
                        <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-overflow-hidden">
                            <table className="tw-w-full tw-text-sm">
                                <thead>
                                    <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200">
                                        <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500">Charge Head</th>
                                        <th className="tw-text-right tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-w-28">Amount</th>
                                        <th className="tw-text-right tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-w-24">CGST</th>
                                        <th className="tw-text-right tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-w-24">SGST</th>
                                        {totalIgst > 0 && <th className="tw-text-right tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-w-24">IGST</th>}
                                        <th className="tw-text-right tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-w-28">Total</th>
                                        <th className="tw-w-10"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {lineItemsWithGst.map((item, idx) => (
                                        <tr key={idx} className="tw-border-b tw-border-gray-100">
                                            <td className="tw-px-3 tw-py-2">
                                                <select value={item.tariff_head_id} onChange={(e) => updateLineItem(idx, 'tariff_head_id', e.target.value)} className="tw-w-full tw-px-2 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded tw-text-sm focus:tw-outline-none focus:tw-border-blue-500">
                                                    <option value="">Select</option>
                                                    {chargeHeads.map(h => <option key={h.head_id} value={h.head_id}>{h.head_name}</option>)}
                                                </select>
                                                {item.taxRule && (
                                                    <p className="tw-text-[10px] tw-text-gray-400 tw-mt-0.5 tw-px-1">
                                                        {item.taxRule.rule_name}
                                                    </p>
                                                )}
                                            </td>
                                            <td className="tw-px-3 tw-py-2">
                                                <input type="number" step="0.01" value={item.amount} onChange={(e) => updateLineItem(idx, 'amount', e.target.value)} placeholder="0.00" className="tw-w-full tw-px-2 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded tw-text-sm tw-text-right focus:tw-outline-none focus:tw-border-blue-500" />
                                            </td>
                                            <td className="tw-px-3 tw-py-2 tw-text-right tw-text-xs tw-text-gray-500">{item.cgst > 0 ? item.cgst.toFixed(2) : '-'}</td>
                                            <td className="tw-px-3 tw-py-2 tw-text-right tw-text-xs tw-text-gray-500">{item.sgst > 0 ? item.sgst.toFixed(2) : '-'}</td>
                                            {totalIgst > 0 && <td className="tw-px-3 tw-py-2 tw-text-right tw-text-xs tw-text-gray-500">{item.igst > 0 ? item.igst.toFixed(2) : '-'}</td>}
                                            <td className="tw-px-3 tw-py-2 tw-text-right tw-text-sm tw-font-medium tw-text-gray-700">{item.total > 0 ? item.total.toFixed(2) : '-'}</td>
                                            <td className="tw-px-2 tw-py-2">
                                                <button type="button" onClick={() => removeLineItem(idx)} className="tw-p-1 tw-rounded hover:tw-bg-red-50 tw-text-gray-400 hover:tw-text-red-600"><Icon icon="mdi:close" /></button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="tw-bg-gray-50 tw-border-t tw-border-gray-200">
                                        <td className="tw-px-3 tw-py-2 tw-text-right tw-text-xs tw-font-semibold tw-text-gray-500">Subtotal:</td>
                                        <td className="tw-px-3 tw-py-2 tw-text-right tw-font-medium tw-text-gray-700">{totalBase.toFixed(2)}</td>
                                        <td className="tw-px-3 tw-py-2 tw-text-right tw-text-xs tw-text-gray-500">{totalCgst > 0 ? totalCgst.toFixed(2) : '-'}</td>
                                        <td className="tw-px-3 tw-py-2 tw-text-right tw-text-xs tw-text-gray-500">{totalSgst > 0 ? totalSgst.toFixed(2) : '-'}</td>
                                        {totalIgst > 0 && <td className="tw-px-3 tw-py-2 tw-text-right tw-text-xs tw-text-gray-500">{totalIgst.toFixed(2)}</td>}
                                        <td className={`tw-px-3 tw-py-2 tw-text-right tw-font-bold tw-text-${accentColor}-600`}>{totalAmount.toFixed(2)}</td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    ) : (
                        <p className="tw-text-sm tw-text-gray-500 tw-text-center tw-py-6">No line items added. Click "Add Item" to add charges.</p>
                    )}

                    {itemsError && (
                        <p className="tw-mt-3 tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-red-600">
                            <Icon icon="mdi:alert-circle-outline" className="tw-text-sm" />
                            {itemsError}
                        </p>
                    )}

                    {/* GST Summary */}
                    {(totalCgst > 0 || totalSgst > 0 || totalIgst > 0) && (
                        <div className="tw-mt-4 tw-flex tw-justify-end">
                            <div className="tw-w-72 tw-bg-gray-50 tw-rounded-lg tw-border tw-border-gray-200 tw-p-3 tw-space-y-1.5">
                                <div className="tw-flex tw-justify-between tw-text-xs tw-text-gray-600">
                                    <span>Base Amount</span>
                                    <span className="tw-font-medium">{formatCurrency(totalBase)}</span>
                                </div>
                                {totalCgst > 0 && (
                                    <div className="tw-flex tw-justify-between tw-text-xs tw-text-gray-500">
                                        <span>CGST</span>
                                        <span>{formatCurrency(totalCgst)}</span>
                                    </div>
                                )}
                                {totalSgst > 0 && (
                                    <div className="tw-flex tw-justify-between tw-text-xs tw-text-gray-500">
                                        <span>SGST</span>
                                        <span>{formatCurrency(totalSgst)}</span>
                                    </div>
                                )}
                                {totalIgst > 0 && (
                                    <div className="tw-flex tw-justify-between tw-text-xs tw-text-gray-500">
                                        <span>IGST</span>
                                        <span>{formatCurrency(totalIgst)}</span>
                                    </div>
                                )}
                                {roundOff !== 0 && (
                                    <div className="tw-flex tw-justify-between tw-text-xs tw-text-gray-500">
                                        <span>Round Off</span>
                                        <span>{roundOff > 0 ? '+' : ''}{roundOff.toFixed(2)}</span>
                                    </div>
                                )}
                                <div className={`tw-flex tw-justify-between tw-text-sm tw-font-bold tw-text-${accentColor}-600 tw-border-t tw-border-gray-200 tw-pt-1.5`}>
                                    <span>Grand Total</span>
                                    <span>{formatCurrency(totalAmount)}</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="tw-flex tw-items-center tw-justify-end tw-gap-3">
                    <button type="button" onClick={() => navigate(-1)} className="tw-px-4 tw-py-2 tw-bg-gray-200 tw-text-gray-700 tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-gray-300">Cancel</button>
                    <button type="submit" disabled={submitLoading || !!dueDateError || !!itemsError} className={`tw-flex tw-items-center tw-gap-2 tw-px-5 tw-py-2 tw-bg-${accentColor}-600 tw-text-white tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-${accentColor}-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed`}>
                        <Icon icon={submitLoading ? "mdi:loading" : "mdi:check"} className={submitLoading ? 'tw-animate-spin' : ''} />
                        {submitLoading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? `Update ${noteLabel}` : `Create ${noteLabel}`)}
                    </button>
                </div>
            </form>}
        </>
    );
};

export default CreateCreditDebitNote;
